import * as _ from 'lodash';

import {
  finleyAssertUnreachable,
  finleyAssert,
} from '@finley-technologies/common';
import { FinleyAi as FinleyAiTypes } from '@finley-technologies/finley-types';

import { FinleyAiViewContext } from './types';

export const finleyAiSelectOptions: {
  value: FinleyAiTypes.FinleyAiType;
  label: string;
}[] = [
  { value: 'athenaSqlGeneration' as const, label: 'SQL Generation' },
  { value: 'documentAma' as const, label: 'Document AMA' },
  { value: 'extractValues' as const, label: 'Extract Values' },
  { value: 'virtualSheetConfig' as const, label: 'Virtual Sheet' },
  { value: 'transform' as const, label: 'Transform' },
];

export const returnIsThreadFailed = (
  thread: FinleyAiTypes.FinleyAiThreadWithPromptResponses,
): boolean => {
  return (
    thread.promptResponses.some((pr) => pr.status === 'failure') ||
    !thread.promptResponses.length
  );
};

export const returnIsTerminalStatus = (
  threadWithPromptResponses: FinleyAiTypes.FinleyAiThreadWithPromptResponses,
) => {
  if (returnIsThreadFailed(threadWithPromptResponses)) {
    return true;
  }

  const lastPromptResponse =
    threadWithPromptResponses.promptResponses[
      threadWithPromptResponses.promptResponses.length - 1
    ];
  finleyAssert(
    lastPromptResponse,
    'Last Finley Ai Prompt Response must exist if thread is in failed state.',
  );

  return FinleyAiTypes.FinleyAiPromptResponseTerminalStatuses.includes(
    lastPromptResponse.status,
  );
};

export const getPromptPlaceholder = ({
  type,
  loading,
  isThreadFailed,
}: {
  type: FinleyAiTypes.FinleyAiType;
  loading: boolean;
  isThreadFailed: boolean;
}): string => {
  if (loading) {
    return 'Thinking...';
  }

  if (isThreadFailed) {
    return 'This thread is in a failed state. Please start a new thread.';
  }
  switch (type) {
    case 'athenaSqlGeneration':
    case 'documentAma':
      return 'Ask a question';
    case 'extractValues':
      return 'Use sidebar to configure extract values.';
    case 'virtualSheetConfig':
      return 'Use sidebar to configure virtual sheet.';
    case 'transform':
      return 'Provide a prompt and use sidebar to configure transform.';
    default:
      return finleyAssertUnreachable(type);
  }
};

export const convertToFinleyAiRequest = ({
  viewContext,
  prompt,
}: {
  viewContext: FinleyAiViewContext;
  prompt: string;
}): FinleyAiTypes.FinleyAiRequest => {
  switch (viewContext.type) {
    case 'athenaSqlGeneration':
      return {
        type: 'athenaSqlGeneration',
        subType: 'sqlGeneration',
        targetOrganizationId: viewContext.targetOrganizationId,
        tableNames: viewContext.tableNames,
        prompt,
        finleyAiThreadId: viewContext.finleyAiThreadId,
        promptTemplate: viewContext.promptTemplate,
      };
    case 'documentAma':
      return {
        type: 'documentAma',
        subType: 'question',
        targetOrganizationId: viewContext.targetOrganizationId,
        knowledgeBaseDocumentIds: viewContext.knowledgeBaseDocumentIds,
        prompt,
        finleyAiThreadId: viewContext.finleyAiThreadId,
        tool: viewContext.tool !== '' ? JSON.parse(viewContext.tool) : null,
        keywords:
          viewContext.keywords && viewContext.keywords.length > 0
            ? viewContext.keywords
            : null,
      };
    case 'extractValues':
      return {
        type: 'extractValues',
        subType: 'extractJson',
        targetOrganizationId: viewContext.targetOrganizationId,
        questionConfigs: viewContext.questionConfigs,
        s3Bucket: viewContext.s3Bucket,
        s3Path: viewContext.s3Path,
        finleyAiThreadId: viewContext.finleyAiThreadId,
        extractionEngine: viewContext.extractionEngine,
        usePages: viewContext.usePages,
      };
    case 'virtualSheetConfig':
      return {
        type: 'virtualSheetConfig',
        subType: 'generateVirtualSheetConfig',
        targetOrganizationId: viewContext.targetOrganizationId,
        questionConfigs: viewContext.questionConfigs,
        s3Bucket: viewContext.s3Bucket,
        s3Path: viewContext.s3Path,
        finleyAiThreadId: null,
        prompt,
      };
    case 'transform':
      return {
        type: 'transform',
        subType: 'generalTransform',
        targetOrganizationId: viewContext.targetOrganizationId,
        finleyAiThreadId: viewContext.finleyAiThreadId,
        prompt,
        documents: viewContext.documents,
        tool:
          viewContext.tool !== '' ? JSON.parse(viewContext.tool) : undefined,
        output: viewContext.output ?? undefined,
        llm: viewContext.llm ?? undefined,
      };

    default:
      return finleyAssertUnreachable(viewContext);
  }
};

export const initFinleyAiViewContext = ({
  type,
  targetOrganizationId,
  promptTemplate,
  knowledgeBaseDocumentIds,
  tool,
  keywords,
}: {
  type: FinleyAiTypes.FinleyAiType;
  targetOrganizationId: string;
  promptTemplate?: string;
  knowledgeBaseDocumentIds?: string[];
  tool?: string;
  keywords?: string[];
}): FinleyAiViewContext => {
  switch (type) {
    case 'athenaSqlGeneration':
      return {
        targetOrganizationId,
        type,
        finleyAiThreadId: null,
        tableNames: [],
        promptTemplate: promptTemplate ?? null,
      };
    case 'documentAma':
      return {
        targetOrganizationId,
        type,
        finleyAiThreadId: null,
        knowledgeBaseDocumentIds: knowledgeBaseDocumentIds ?? [],
        tool: tool ?? '',
        keywords: keywords ?? [],
      };
    case 'extractValues':
      return {
        targetOrganizationId,
        type,
        subType: 'extractJson',
        finleyAiThreadId: null,
        s3Bucket: '',
        s3Path: '',
        extractionEngine: 'default',
        questionConfigs: [],
      };
    case 'virtualSheetConfig':
      return {
        targetOrganizationId,
        type,
        finleyAiThreadId: null,
        s3Bucket: '',
        s3Path: '',
        questionConfigs: [],
      };

    case 'transform':
      return {
        targetOrganizationId,
        type,
        finleyAiThreadId: null,
        documents: [],
        tool: '',
        llm: null,
        output: null,
      };

    default:
      return finleyAssertUnreachable(type);
  }
};

export const generateFinleyAiViewContextFromThread = ({
  thread,
}: {
  thread: FinleyAiTypes.FinleyAiThreadWithPromptResponses;
}): FinleyAiViewContext => {
  const { id: finleyAiThreadId } = thread;

  switch (thread.type) {
    case 'athenaSqlGeneration': {
      // we just need to bootstrap the tablenames for this thread
      // any sqlGeneration subtype will do, as tablenames are static once
      // a thread is created
      const sqlGenerationPromptResponse = _.find(
        thread.promptResponses,
        (pr) => pr.contextMetadata.subType === 'sqlGeneration',
      );

      finleyAssert(
        sqlGenerationPromptResponse,
        'Unable set Finley Ai View Context. Expected sqlGeneration prompt response to exist.',
      );

      finleyAssert(
        sqlGenerationPromptResponse.contextMetadata.subType ===
          'sqlGeneration' &&
          sqlGenerationPromptResponse.rawPrompt.subType === 'sqlGeneration',
        `Context metadata subtype must be sqlGeneration, but got [subType:${sqlGenerationPromptResponse.contextMetadata.subType}]`,
      );
      return {
        targetOrganizationId: thread.targetOrganizationId,
        type: thread.type,
        finleyAiThreadId,
        tableNames: sqlGenerationPromptResponse.contextMetadata.tables.map(
          (table) => table.tableName,
        ),
        promptTemplate: sqlGenerationPromptResponse.rawPrompt.promptTemplate,
      };
    }
    case 'documentAma': {
      finleyAssert(
        thread.promptResponses.length > 0,
        'Unable to set Finley Ai View Context for Document AMA. Expected at least one prompt response to exist.',
      );
      // re-use the configuration from the latest prompt response, instead of reseting to first one
      const pr = thread.promptResponses[thread.promptResponses.length - 1];

      let knowledgeBaseDocumentIds: string[] = [];
      let tool = '';
      let keywords: string[] = [];
      switch (pr.subType) {
        case 'question':
          knowledgeBaseDocumentIds =
            pr.contextMetadata.knowledgeBaseDocumentIds;
          tool = pr.contextMetadata.tool
            ? JSON.stringify(pr.contextMetadata.tool, null, 2)
            : '';
          keywords = pr.contextMetadata.keywords ?? [];

          break;
        // while upload is in progress, the knowledgeBaseDocumentId isn't known yet
        // this is okay, since the table selection is locked up (and initially empty)
        // when uploading a document for the first time
        case 'upload':
          knowledgeBaseDocumentIds = pr.promptResult?.knowledgeBaseDocumentId
            ? [pr.promptResult.knowledgeBaseDocumentId]
            : [];
          break;
        default:
          return finleyAssertUnreachable(pr);
      }
      return {
        targetOrganizationId: thread.targetOrganizationId,
        type: thread.type,
        finleyAiThreadId,
        knowledgeBaseDocumentIds,
        tool,
        keywords,
      };
    }
    case 'extractValues': {
      // there should always be a last prompt response to bootstrap the thread context
      const lastPr = _.last(thread.promptResponses);
      finleyAssert(
        lastPr,
        'Unable to set Finley Ai View Context for Extract Values. Expected at least one prompt response to exist.',
      );
      return {
        targetOrganizationId: thread.targetOrganizationId,
        type: thread.type,
        subType: lastPr.subType,
        finleyAiThreadId,
        extractionEngine: lastPr.contextMetadata.extractionEngine,
        s3Bucket: lastPr.contextMetadata.s3Bucket,
        s3Path: lastPr.contextMetadata.s3Path,
        questionConfigs: lastPr.contextMetadata.questionConfigs,
        usePages: lastPr.contextMetadata.usePages,
      };
    }
    case 'virtualSheetConfig': {
      // there should always be a last prompt response to bootstrap the thread context
      const lastPr = _.last(thread.promptResponses);
      finleyAssert(
        lastPr,
        'Unable to set Finley Ai View Context for Extract Values. Expected at least one prompt response to exist.',
      );
      return {
        targetOrganizationId: thread.targetOrganizationId,
        type: thread.type,
        finleyAiThreadId,
        s3Bucket: lastPr.contextMetadata.s3Bucket,
        s3Path: lastPr.contextMetadata.s3Path,
        questionConfigs: lastPr.contextMetadata.questionConfigs,
      };
    }

    case 'transform': {
      // there should always be a last prompt response to bootstrap the thread context
      const lastPr = _.last(thread.promptResponses);
      finleyAssert(
        lastPr,
        'Unable to set Finley Ai View Context for Transform. Expected at least one prompt response to exist.',
      );
      return {
        targetOrganizationId: thread.targetOrganizationId,
        type: thread.type,
        finleyAiThreadId,
        documents: lastPr.contextMetadata.documents,
        llm: lastPr.contextMetadata.llm ?? null,
        output: lastPr.contextMetadata.output ?? null,
        tool: lastPr.contextMetadata.tool
          ? JSON.stringify(lastPr.contextMetadata.tool, null, 2)
          : '',
      };
    }

    default:
      return finleyAssertUnreachable(thread);
  }
};

interface ParsedPromptResponse {
  value: string;
  type: 'sql' | 'plaintext';
}

// parses prompt result response into segments so that SQL can be rendered separately
export const parsePromptResponse = (input: string): ParsedPromptResponse[] => {
  const regex = /(<SQL>[\s\S]*?<\/SQL>)/;
  const parts = input.split(regex);
  const result: ParsedPromptResponse[] = [];

  parts.forEach((part) => {
    if (part.startsWith('<SQL>') && part.endsWith('</SQL>')) {
      result.push({
        value: part.slice(5, -6), // remove '<SQL>' and '</SQL>'
        type: 'sql',
      });
    } else {
      result.push({
        value: part.trimStart(), // remove leading linebreaks
        type: 'plaintext',
      });
    }
  });

  return result;
};

// monaco-editor does not natively support fit-content height
// so we will calculate the height the editor should roughly be
// based on the number of lines in the string it's displaying
export const getEditorHeight = (str: string) => {
  const numberOfLinesInString = (str.match(/\n/g) || []).length + 1;
  return `${numberOfLinesInString * 24}px`;
};

export const numberOfSqlResultRowsToPreview = 50;

export const getPreviewDataRowCountLabel: (
  totalPreviewCsvRowCount: number,
) => string = (totalPreviewCsvRowCount) => {
  const moreRowsExistThanPreview =
    totalPreviewCsvRowCount > numberOfSqlResultRowsToPreview;

  return moreRowsExistThanPreview
    ? `Displaying first ${numberOfSqlResultRowsToPreview} rows`
    : `Displaying all ${totalPreviewCsvRowCount} rows`;
};

export const getAbbreviatedThreadTitle = (threadTitle: string) => {
  if (threadTitle.length <= 100) {
    return threadTitle;
  }
  return `${threadTitle.substring(0, 100)}...`;
};

export type OnClickAthenaSqlGenerationPromptSuggestion = (
  rawPrompt: string,
) => void;
